/**
 * Кнопка «Передать креды зашифровано» — для staff в композере тикета.
 *
 * Поток:
 *  1. Открывается модалка с textarea для plaintext, label и опциями
 *     (max_views, ttl_minutes).
 *  2. На клик «Создать»:
 *     a. Генерируем random 256-bit AES-GCM key, 12-byte nonce.
 *     b. Шифруем plaintext.
 *     c. POST /api/v1/secrets с ciphertext+nonce (без ключа!).
 *     d. Получаем {id}.
 *     e. Формируем ссылку  https://<host>/secret/<id>#<key_b64>.
 *  3. Возвращаем ссылку в callback (для вставки в композер).
 */
import { useState } from 'react';
import { ShieldAlert, Loader2, X } from 'lucide-react';
import { api } from '../api/client';

function bytesToB64Url(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export default function SecretShareButton({
  ticketId, organizationId, onLink,
}: {
  ticketId?: string;
  organizationId?: string;
  onLink: (link: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  const [maxViews, setMaxViews] = useState(1);
  const [ttlMinutes, setTtlMinutes] = useState(1440);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text) return;
    setBusy(true); setErr(null);
    try {
      // 1. Ключ + nonce
      const keyBytes = crypto.getRandomValues(new Uint8Array(32));
      const nonce = crypto.getRandomValues(new Uint8Array(12));
      const key = await crypto.subtle.importKey(
        'raw', keyBytes, { name: 'AES-GCM' }, false, ['encrypt'],
      );
      // 2. Шифруем
      const ct = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: nonce }, key, new TextEncoder().encode(text),
      );
      // 3. POST
      const r = await api.post<{ id: string; expires_at: string; max_views: number }>(
        '/api/v1/secrets', {
          ciphertext_b64: bytesToB64Url(ct),
          nonce_b64: bytesToB64Url(nonce),
          label: label || null,
          organization_id: organizationId || null,
          ticket_id: ticketId || null,
          max_views: maxViews,
          ttl_minutes: ttlMinutes,
        },
      );
      const link = `${window.location.origin}/secret/${r.data.id}#${bytesToB64Url(keyBytes)}`;
      onLink(link);
      setOpen(false);
      setText(''); setLabel('');
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'ошибка');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        title="Передать пароль/ключ в зашифрованном виде (zero-knowledge)"
        className="inline-flex items-center gap-1 bg-amber-700 hover:bg-amber-600 text-white px-2 py-1 rounded text-xs">
        <ShieldAlert size={12} /> Передать креды
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={() => setOpen(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <header className="p-4 border-b border-slate-800 flex justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <ShieldAlert className="text-amber-400" size={16} /> Передать креды клиенту
              </h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </header>
            <form onSubmit={create} className="p-4 space-y-3">
              <label className="block">
                <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                  Содержимое (логин/пароль/ключ — что угодно)
                </span>
                <textarea value={text} onChange={e => setText(e.target.value)}
                  required rows={5} autoFocus
                  placeholder="login: admin&#10;password: SuperSecret123"
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white font-mono" />
              </label>
              <label className="block">
                <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                  Метка (видна получателю до открытия, без секрета!)
                </span>
                <input value={label} onChange={e => setLabel(e.target.value)}
                  placeholder="Креды от админки backup-сервера"
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Просмотров</span>
                  <input type="number" min={1} max={20} value={maxViews}
                    onChange={e => setMaxViews(parseInt(e.target.value || '1', 10))}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
                </label>
                <label className="block">
                  <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Жизнь (минут)</span>
                  <input type="number" min={1} max={10080} value={ttlMinutes}
                    onChange={e => setTtlMinutes(parseInt(e.target.value || '60', 10))}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
                </label>
              </div>
              {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-xs">{err}</div>}
              <p className="text-[10px] text-slate-500">
                Содержимое шифруется в вашем браузере 256-битным AES-GCM. На сервер уходит
                только ciphertext. Ключ — в URL fragment получателя, его не видит сервер.
                Ссылка вставится в композер ответа тикета — отправьте её клиенту обычным
                сообщением.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
                <button type="submit" disabled={busy || !text}
                  className="px-4 py-2 bg-amber-700 hover:bg-amber-600 disabled:bg-slate-700 rounded text-white text-sm font-bold inline-flex items-center gap-1.5">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} />}
                  Создать ссылку
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
